import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

const KEY = "lpc.settings.open";
const load = () => { try { return new Set(JSON.parse(sessionStorage.getItem(KEY) || "[]")); } catch { return new Set(); } };

/** Which Settings sections are unfolded — remembered for the browser session. */
export function useSettingsFolds(initialOpen = []) {
  const [open, setOpen] = useState(() => { const s = load(); initialOpen.forEach((k) => k && s.add(k)); return s; });
  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify([...open])); }, [open]);
  const toggle = useCallback((id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }), []);
  const setAll = useCallback((ids) => setOpen(new Set(ids)), []);
  return { isOpen: (id) => open.has(id), toggle, setAll, count: open.size };
}

/** One Settings section: a title line; press it to unfold the card underneath (its own title is hidden to avoid repeating). */
export function SettingsFold({ id, icon, title, tone = "default", folds, children }) {
  const open = folds.isOpen(id);
  const danger = tone === "danger";
  return (
    <section
      id={`fold-${id}`}
      className={`rounded-lg border bg-white overflow-hidden ${danger ? "border-red-300" : open ? "border-slate-300" : "border-slate-200"}`}
      data-testid={`fold-${id}`}
      data-state={open ? "open" : "closed"}
    >
      <button
        type="button"
        onClick={() => folds.toggle(id)}
        aria-expanded={open}
        className={`w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors ${danger ? "hover:bg-red-50" : "hover:bg-slate-50"}`}
        data-testid={`fold-${id}-toggle`}
      >
        <span className="text-xl w-7 text-center shrink-0" aria-hidden>{icon}</span>
        <span className={`font-semibold ${danger ? "text-red-800" : "text-slate-800"}`}>{title}</span>
        <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="settings-fold-body border-t border-slate-200 overflow-x-auto [&>*]:!border-0 [&>*]:!rounded-none [&>*]:!shadow-none" data-testid={`fold-${id}-body`}>
          {children}
        </div>
      )}
    </section>
  );
}
